/**
 * mandelbrotPipeline — real-time zoom into the Mandelbrot fractal.
 *
 * The classic Mandelbrot set: z = z² + c. We sample at high precision
 * using a smooth iteration count (continuous escape time) and color
 * the exterior with a palindrome palette. The view zooms in
 * continuously, exploring an infinite zoom path through the
 * "seahorse valley" region. Audio drives the palette shift and zoom speed.
 *
 * No mesh, no simulation — single fullscreen quad fragment shader.
 *
 * Attribution: shader logic is a classical Mandelbrot implementation.
 * NSPP integration + audio reactivity mine.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface MandelbrotPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Zoom path: continuous spiral zoom into a region of interest
  // centered on (-0.745, 0.113) — the famous "seahorse valley".
  // We use log-scale zoom so the path accelerates smoothly.
  float zoom = exp(uTime * 0.18 * (0.5 + bass * 0.6));
  // Slight rotation as we zoom (gives the feeling of motion).
  float rot = uTime * 0.05;
  mat2 rotM = mat2(cos(rot), -sin(rot), sin(rot), cos(rot));
  // Map screen coords to complex plane.
  vec2 uv = (gl_FragCoord.xy - uResolution * 0.5) / (min(uResolution.x, uResolution.y) * 0.5);
  uv = rotM * uv;
  // Scale and translate to the region of interest.
  vec2 c = uv / zoom + vec2(-0.745, 0.113);

  // Mandelbrot iteration.
  const int MAX_ITER = 120;
  vec2 z = vec2(0.0);
  float n = 0.0;
  float smoothN = 0.0;
  bool escaped = false;
  for (int i = 0; i < 120; i++) {
    if (i >= MAX_ITER) break;
    z = vec2(z.x * z.x - z.y * z.y, 2.0 * z.x * z.y) + c;
    float r2 = dot(z, z);
    if (r2 > 256.0) {
      escaped = true;
      // Smooth iteration count (continuous escape time).
      smoothN = float(i) - log2(log2(r2)) + 4.0;
      n = smoothN;
      break;
    }
  }

  vec3 col;
  if (!escaped) {
    // Inside the set: deep dark.
    col = vec3(0.01, 0.01, 0.04);
  } else {
    // Outside: smooth palette. Use the smooth iteration count with
    // log scaling so colors are smooth across iteration bands.
    float t = log2(n + 1.0) * 0.3 + uTime * 0.05;
    vec3 c1 = vec3(0.05, 0.10, 0.40);  // deep blue
    vec3 c2 = vec3(0.20, 0.55, 0.85);  // cyan
    vec3 c3 = vec3(0.95, 0.45, 0.15);  // orange
    vec3 c4 = vec3(1.00, 0.95, 0.55);  // yellow
    vec3 c5 = vec3(0.85, 0.10, 0.55);  // magenta
    col = mix(c1, c2, smoothstep(0.0, 0.4, fract(t)));
    col = mix(col, c3, smoothstep(0.4, 0.7, fract(t)));
    col = mix(col, c4, smoothstep(0.7, 0.9, fract(t)));
    col = mix(col, c5, smoothstep(0.9, 1.1, fract(t)));
    // Audio: shift palette.
    col = mix(col, vec3(c5.r, c4.g, c3.b), high * 0.3);
  }

  // Vignette.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float vig = 1.0 - dot(p, p) * 0.3;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeMandelbrotBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.3);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.5 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 0.8 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.0 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.2 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.5 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 1.9 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.3 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 2.8 + 3.0);
}

export function computeMandelbrotRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createMandelbrotPipeline(gl: WebGL2RenderingContext): MandelbrotPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawMandelbrot(
  gl: WebGL2RenderingContext,
  pipe: MandelbrotPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
